import React, { useState } from "react";
import clsx from "clsx";
import {
  Button,
  ButtonVariant,
  Card,
  CopyTextField,
  Dialog,
  Link,
  Text,
  TextField,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import { fetchWithAuth } from "../../../api-client/APIClient";
import { showErrorToast } from "../../shared/Toasts";
import useAppContext from "../../context/useAppContext";
import { UserType } from "../../../models/Entities";
import { API_KEYS_PATH } from "../../../router/RouterUtils";
import ToolTabbedCodeSnippet from "./components/ToolTabbedCodeSnippet";
import LeftSideBar from "./components/shared/LeftSideBar";
import { PaddingLessCol } from "./components/shared/LeftSideBarSpacing";
import { ReactComponent as AIWand } from "./assets/ai-wand.svg";
import { ReactComponent as DocsBook } from "./assets/docs-book.svg";

const TOOL_OPTIONS = [
  { label: "Claude", value: "claude" },
  { label: "Cursor", value: "cursor" },
  { label: "Codex", value: "codex" },
  { label: "Windsurf", value: "windsurf" },
  { label: "Aider", value: "aider" },
  { label: "Kilo Code", value: "kilocode" },
  { label: "OpenCode", value: "opencode" },
  { label: "Augment", value: "augment" },
];

const DOCS_MCP_TOOL_OPTIONS = [
  { label: "Claude", value: "claude" },
  { label: "Cursor", value: "cursor" },
  { label: "Codex", value: "codex" },
];

const DOCS_MCP_SNIPPETS: Record<string, string> = {
  claude: "claude mcp add --transport http docs-merge-dev https://docs.merge.dev/_mcp/server",
  codex: "codex mcp add docs-merge-dev --url https://docs.merge.dev/_mcp/server",
  cursor: JSON.stringify(
    { mcpServers: { "docs-merge-dev": { url: "https://docs.merge.dev/_mcp/server" } } },
    null,
    2,
  ),
  windsurf: JSON.stringify(
    { mcpServers: { "docs-merge-dev": { serverUrl: "https://docs.merge.dev/_mcp/server" } } },
    null,
    2,
  ),
  vscode: JSON.stringify(
    { servers: { "docs-merge-dev": { type: "http", url: "https://docs.merge.dev/_mcp/server" } } },
    null,
    2,
  ),
};

const convertSnippet = (tool: string) =>
  [
    "git clone https://github.com/merge-api/merge-unified-skills.git",
    "cd merge-unified-skills",
    `./scripts/convert.sh --tool ${tool} --target /path/to/your/project`,
  ].join("\n");

// The committed skills/ directory is already in the format Codex reads, so no convert step is
// needed: clone the repo and copy the skills into the project's .codex/skills directory.
const codexSnippet = [
  "git clone https://github.com/merge-api/merge-unified-skills.git",
  "mkdir -p /path/to/your/project/.codex/skills && cp -r merge-unified-skills/skills/* /path/to/your/project/.codex/skills/",
].join("\n");

const SNIPPETS: Record<string, string> = {
  claude: [
    "claude plugin marketplace add merge-api/merge-unified-skills",
    "claude plugin install merge-unified",
  ].join("\n"),
  cursor: convertSnippet("cursor"),
  codex: codexSnippet,
  windsurf: convertSnippet("windsurf"),
  aider: convertSnippet("aider"),
  kilocode: convertSnippet("kilocode"),
  opencode: convertSnippet("opencode"),
  augment: convertSnippet("augment"),
};

// JSON config snippets highlight as JSON (keys vs. values, meaningful). Terminal commands
// use "text": CodeSnippet's highlighter (prism-react-renderer) maps "bash"/"shell" to a bash
// grammar that only colors stray builtins (e.g. `git`, `cd`, `add`) and reads as broken on a
// plain command. "text" is the highlighter's built-in plaintext grammar, so the block renders
// uniformly plain. (An unregistered name like "sh" renders an empty body, not plain text.)
const snippetLanguage = (code: string) => (code.trim().startsWith("{") ? "json" : "text");

const ADMIN_REQUIRED_TOOLTIP = "Only admins with billing access can generate API keys.";

type DialogMode = "test" | "production";

interface APIKey {
  id: string;
  key: string;
}

interface Props {
  standalone?: boolean;
}

const AIPluginsBanner = ({ standalone }: Props) => {
  const { user } = useAppContext();
  const userIsNotAdminWithBilling = user.type !== UserType.admin_with_billing;

  const [selectedTool, setSelectedTool] = useState("claude");
  const [selectedDocsTool, setSelectedDocsTool] = useState("claude");
  const [testKey, setTestKey] = useState<string | undefined>();
  const [productionKey, setProductionKey] = useState<string | undefined>();
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [keyName, setKeyName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const closeDialog = () => {
    setDialogMode(null);
    setKeyName("");
    setIsCreating(false);
  };

  const createKey = () => {
    if (isCreating || !dialogMode || !keyName.trim()) return;
    setIsCreating(true);
    fetchWithAuth({
      path: "/integrations/remote-key",
      method: "POST",
      body: { name: keyName, is_test: dialogMode === "test" },
      onResponse: (generated: APIKey) => {
        if (dialogMode === "test") {
          setTestKey(generated.key);
        } else {
          setProductionKey(generated.key);
        }
        closeDialog();
      },
      onError: (response: Response | undefined) => {
        if (response && response.status === 409) {
          showErrorToast(
            `A key named "${keyName}" already exists. Please choose a different name.`,
          );
        } else {
          showErrorToast(`Failed to generate ${dialogMode} API key. Please try again.`);
        }
        setIsCreating(false);
      },
    });
  };

  const dialogTitle =
    dialogMode === "test"
      ? "New test API key"
      : dialogMode === "production"
      ? "New production API key"
      : "";

  const renderGenerateButton = (mode: DialogMode, label: string) => {
    const button = (
      <Button
        variant={ButtonVariant.TertiaryWhite}
        size="sm"
        ariaLabel={`Generate ${mode} API key`}
        onClick={() => setDialogMode(mode)}
        disabled={userIsNotAdminWithBilling}
      >
        {label}
      </Button>
    );
    return userIsNotAdminWithBilling ? (
      <Tooltip title={ADMIN_REQUIRED_TOOLTIP}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <div className={clsx("flex flex-col mx-auto w-full", !standalone && "mb-8")}>
      <Text variant="h3" className="mb-6">
        Use AI plugins
      </Text>

      {/* Step 1 — Install */}
      <div className="flex flex-row">
        <LeftSideBar stepNumber="1" isNormalStep />
        <PaddingLessCol>
          <Card className="mb-6 p-6">
            <div className="mb-3 flex flex-row items-center gap-x-4">
              <div className="shrink-0">
                <AIWand />
              </div>
              <div className="flex flex-col">
                <Text variant="h4" className="mb-1">
                  Install the AI coding skills
                </Text>
                <Text>
                  Install our AI coding skills to ship your first integration in minutes. Visit our{" "}
                  <Link href="https://github.com/merge-api/merge-unified-skills" target="_blank">
                    repo to learn more
                  </Link>
                  .
                </Text>
              </div>
            </div>
            <Text>Select your AI tool and run the following command in your project:</Text>
            <div className="h-3" />
            <ToolTabbedCodeSnippet
              options={TOOL_OPTIONS}
              selectedValue={selectedTool}
              onSelect={setSelectedTool}
              code={SNIPPETS[selectedTool]}
              language={snippetLanguage(SNIPPETS[selectedTool])}
              ariaLabel="AI tool"
            />
          </Card>
        </PaddingLessCol>
      </div>

      {/* Step 2 — API keys */}
      <div className="flex flex-row">
        <LeftSideBar stepNumber="2" isNormalStep />
        <PaddingLessCol>
          <Card className="mb-6 p-6">
            <Text variant="h4" className="mb-1">
              API keys
            </Text>
            <Text>
              When you&apos;re ready to make real API calls, generate keys for the AI plugin to use.
              You can also manage them on the <Link href={API_KEYS_PATH}>API keys page</Link>.
            </Text>
            <div className="h-6" />
            <div className="overflow-hidden rounded-lg border border-gray-10">
              <div className="p-4">
                <div className="flex flex-row items-center justify-between gap-x-4">
                  <Text variant="h6">Test API key</Text>
                  {!testKey && renderGenerateButton("test", "Generate")}
                </div>
                {testKey && (
                  // Generated test API key is a secret and is thus masked at the field (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot)
                  <div className="ph-no-capture mt-3" data-fr-ignore>
                    <CopyTextField text={testKey} secret />
                  </div>
                )}
              </div>
              <div className="border-t border-gray-10" />
              <div className="p-4">
                <div className="flex flex-row items-center justify-between gap-x-4">
                  <Text variant="h6">Production API key</Text>
                  {!productionKey && renderGenerateButton("production", "Generate")}
                </div>
                {productionKey && (
                  // Generated production API key is a secret and is thus masked at the field (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot)
                  <div className="ph-no-capture mt-3" data-fr-ignore>
                    <CopyTextField text={productionKey} secret />
                  </div>
                )}
              </div>
            </div>
            {(testKey || productionKey) && (
              <Text className="mt-4">
                Save these keys somewhere safe. You won&apos;t be able to view them again after
                leaving this page.
              </Text>
            )}
          </Card>
        </PaddingLessCol>
      </div>

      {/* Step 3 — Docs MCP */}
      <div className="flex flex-row">
        <LeftSideBar stepNumber="3" isNormalStep={false} />
        <PaddingLessCol>
          <Card className="p-6">
            <div className="mb-3 flex flex-row items-center gap-x-4">
              <div className="shrink-0">
                <DocsBook />
              </div>
              <div className="flex flex-col">
                <Text variant="h4" className="mb-1">
                  Connect to Merge Docs via MCP
                </Text>
                <Text>
                  Connect your agent to Merge&apos;s docs through MCP to get answers for your
                  specific use case.
                </Text>
              </div>
            </div>
            <Text>Select your AI agent and run the following command in your project:</Text>
            <div className="h-3" />
            <ToolTabbedCodeSnippet
              options={DOCS_MCP_TOOL_OPTIONS}
              selectedValue={selectedDocsTool}
              onSelect={setSelectedDocsTool}
              code={DOCS_MCP_SNIPPETS[selectedDocsTool]}
              language={snippetLanguage(DOCS_MCP_SNIPPETS[selectedDocsTool])}
              ariaLabel="Docs MCP tool"
            />
          </Card>
        </PaddingLessCol>
      </div>

      <Dialog
        open={dialogMode !== null}
        onClose={closeDialog}
        title={dialogTitle}
        variant="md"
        primaryButtonText="Create"
        primaryButtonVariant={ButtonVariant.PrimaryGray}
        primaryButtonDisabled={!keyName.trim() || isCreating}
        primaryButtonLoading={isCreating}
        onPrimaryButtonClick={createKey}
        secondaryButtonText="Cancel"
        secondaryButtonVariant={ButtonVariant.TextBlack}
        onSecondaryButtonClick={closeDialog}
      >
        <Text>
          This key only provides access to{" "}
          <b>{dialogMode === "test" ? "test" : "production"} Linked Accounts</b> in your Merge
          instance.
        </Text>
        <TextField
          labelTitle="Key name"
          placeholder="e.g. AI plugin key"
          className="mt-4"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
        />
      </Dialog>
    </div>
  );
};

export default AIPluginsBanner;
